import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'

/**
 * Birleşik SSE — TEK EventSource ile tüm realtime kanallarını
 * (quote/crm/tasks/mail/chat/notif) dinler. Eski per-sayfa hook'ların (useQuoteStream/useCrmStream/useTasksStream +
 * CrmInbox mail stream) yerini alır.
 *
 * NEDEN: yerel dağıtım HTTP/1.1 → tarayıcı host başına ~6 kalıcı bağlantı. Her SSE stream
 * bir bağlantı tutar; 4-5 ayrı stream × çoklu sekme limiti tüketip yeni istekleri kuyruğa
 * sokuyordu ("Aranıyor…" takılması). Tek bağlantı bu tavanı rahatlatır.
 *
 * Layout'ta GLOBAL mount edilir (giriş yapıldığında). Backend: GET /api/v1/stream →
 * kanal değişince o kanalın adıyla event (event: quote|crm|tasks|mail).
 *
 * Korunan davranışlar (eski hook'lardan):
 *  - never-cancel-in-flight: aktif fetch varken invalidate etme (cancel→pending döngüsü,
 *    "Aranıyor…"da takılma) — 1.5sn sonra tekrar dene.
 *  - throttle: kanal başına en fazla 4sn'de bir invalidate (art arda update'lerde ilk
 *    yüklemenin iptal edilmemesi).
 *  - güncel token + backoff reconnect: token süresi dolunca kapat, 5sn sonra store'daki
 *    güncel token'la yeniden bağlan.
 */
const CHANNELS = {
  quote: [['quotes'], ['quote']],
  crm: [['crm-deals'], ['crm-pipelines'], ['crm-deal']],
  tasks: [['task-spaces'], ['task-list'], ['task'], ['task-my'], ['task-detail'], ['task-gantt']],
  mail: [['inbox-all-mails'], ['inbox-unread-count'], ['leads'], ['lead-counts']],
  // Sohbet rozeti + sohbet listesi. Mesaj GÖVDELERİ burada DEĞİL: açık sohbeti
  // `useChatStream` zaten 1 sn'lik kendi eşiğiyle tazeliyor; ikisi birden
  // invalidate ederse aynı isteği iki kez kovalar.
  chat: [['chat-unread'], ['chat-conversations'], ['chat-inbox']],
  // Header'daki bildirim popup'ı + zil listesi + bildirim akışı.
  notif: [['notifications-unread-count'], ['notifications'], ['notifications-feed'], ['notifications-panel']],
  // WhatsApp gelen kutusu: konuşma listesi + açık konuşmanın mesajları + rozet.
  whatsapp: [['wa-conversations'], ['wa-messages'], ['wa-conversation'], ['wa-meta']],
}

// Sohbet ve bildirim "yazıldığı gibi" düşmeli; diğer kanallarda 4 sn eşik
// art arda gelen güncellemelerde ilk yüklemeyi iptal etmemek için korunuyor.
const THROTTLE_MS = 4000
const FAST_THROTTLE_MS = 800
const FAST_CHANNELS = new Set(['chat', 'notif', 'whatsapp'])
const throttleFor = (ch) => (FAST_CHANNELS.has(ch) ? FAST_THROTTLE_MS : THROTTLE_MS)

export function useAppStream() {
  const qc = useQueryClient()
  useEffect(() => {
    if (typeof EventSource === 'undefined') return
    let es
    let closed = false
    let reconnectTimer
    // kanal başına { last: son invalidate zamanı, pending: bekleyen timer }
    const st = {}
    Object.keys(CHANNELS).forEach((ch) => { st[ch] = { last: Date.now(), pending: null } })

    const doInvalidate = (ch) => {
      const keys = CHANNELS[ch]
      // Devam eden bir fetch'i ASLA iptal etme (invalidate aktif query'yi cancel+restart eder).
      const anyFetching = keys.some((k) => qc.isFetching({ queryKey: k }) > 0)
      if (anyFetching) {
        if (!st[ch].pending) st[ch].pending = setTimeout(() => { st[ch].pending = null; doInvalidate(ch) }, 1500)
        return
      }
      st[ch].last = Date.now()
      keys.forEach((k) => qc.invalidateQueries({ queryKey: k }))
    }

    const onChannel = (ch) => {
      const ms = throttleFor(ch)
      const since = Date.now() - st[ch].last
      if (since >= ms) doInvalidate(ch)
      else if (!st[ch].pending) st[ch].pending = setTimeout(() => { st[ch].pending = null; doInvalidate(ch) }, ms - since)
    }

    const connect = () => {
      if (closed) return
      const token = useAuthStore.getState().token
      if (!token) { reconnectTimer = setTimeout(connect, 5000); return }
      try {
        es = new EventSource(`/api/v1/stream?token=${encodeURIComponent(token)}`)
        Object.keys(CHANNELS).forEach((ch) => es.addEventListener(ch, () => onChannel(ch)))
        es.onerror = () => {
          try { es.close() } catch { /* yut */ }
          if (!closed) {
            clearTimeout(reconnectTimer)
            reconnectTimer = setTimeout(connect, 5000)
          }
        }
      } catch {
        // EventSource kurulamadı → normal query cache (staleTime) yedek
      }
    }
    connect()

    return () => {
      closed = true
      clearTimeout(reconnectTimer)
      Object.values(st).forEach((s) => clearTimeout(s.pending))
      if (es) es.close()
    }
  }, [qc])
}
